import i18n from "@/lang";
// Narrowed t() signature to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t = (i18n.global as unknown as { t: (key: string) => string }).t;
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { resolveEndpoint } from "@/config/endpoint-resolver";
import ComplianceModule from "@/store/modules/compliance";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import {
  Compliance as ComplianceV2,
  ComplianceOrderSubscriber,
  EventType,
  FiscalSale,
  MoneyInOutType as MoneyInOutTypeV2,
  isErr,
  ok,
  type CompanyLegalInfo,
} from "@smeetz/compliance-common-v2";
import type {
  AbortPayload,
  AuditEventPayload,
  AuditEventType,
  ComplianceError,
  EventOutcome,
  ICompliance,
  ICounterPort,
  ILogger,
  MoneyInOutPayload,
  Operator,
  OrderEventSubscription,
  PrintPayload,
  ReportDailyPayload,
  SalePayload,
} from "@smeetz/compliance-common-v2";
import { MoneyInOutType as MoneyInOutTypeV1 } from "@smeetz/compliance-common";
import type { OrderSession } from "@smeetz/order-common";
import { Order } from "@smeetz/orders";
import type { OrderJson } from "@smeetz/orders";
import { isFrenchCompliant } from "../vue-helpers/compliance";
import { getSmtzAuth, getUserAccessToken } from "./auth";
import { EFR_SERVER_URL, testEfrConnection } from "./compliance";
import { getLanguage } from "./lang";
import { persistComplianceFields } from "./store";

/**
 * France fiscalization on the new order logic (FIS-517), backed by
 * `@smeetz/compliance-common-v2@0.6.x` (order session v2, FIS-486).
 *
 * Covered here (real EFR fiscalization + cert-service audit copy): ABORT,
 * WORK_IN/WORK_OUT, MONEY_IN_OUT, REPORT_DAILY (Z), PRINT (first print +
 * duplicata) and the audit-record family (drawer open, price lookup,
 * suspend/resume, NF525 130) as direct calls — they are not order-bus
 * events. SALE and the NF525 323 line audit are BUS-driven instead: the
 * package's `ComplianceOrderSubscriber` is attached to the order engine's
 * shared bus (`Order.events()`, `@smeetz/orders` >= 2.13) after every
 * `configure()`, fiscalizing `ORDER_SESSION_COMPLETED` as SALE and
 * `ORDER_LINE_PRICE_OVERRIDDEN`/`ORDER_LINE_REMOVED` as 323. The subscriber
 * is fail-open BY DESIGN (accepted product tradeoff): a fiscal failure is
 * logged, never thrown into the bus, so it cannot block order completion —
 * which is also why the operator session must be restored proactively (see
 * `ensureOperatorSessionV2`) rather than lazily at event time. Units and
 * date strings are normalized inside the package — sessions are passed
 * through unconverted. Non-French countries keep going through the v1
 * helpers in `./compliance.ts`.
 */

/**
 * Fiscal transaction-number source injected into the v2 package.
 *
 * Backed by `ComplianceModule.transactionNumber` so the sequence stays ONE
 * monotonic, persisted series per device (FIS-185 lesson: a lazily-persisted
 * counter resets on reload and the EFR flags duplicates), shared with any
 * v1 event using the same counter.
 */
const moduleTransactionCounter: ICounterPort = {
  async next(): Promise<number> {
    const value = ComplianceModule.transactionNumber;
    ComplianceModule.increaseTransactionNumber();
    persistComplianceFields();
    return value;
  },
};

/**
 * Bus subscriptions held by the currently configured v2 instance (SALE +
 * NF525 323 via `ComplianceOrderSubscriber`). The order engine's bus outlives
 * `Compliance` instances, so a re-configure must detach the previous
 * subscriber before attaching the fresh one — two live subscribers would
 * fiscalize every completed session twice.
 */
let orderBusSubscriptions: OrderEventSubscription[] = [];

/** Routes the v2 package's structured logs into the POS log backend. */
const complianceV2Logger: ILogger = {
  info(msg: string, ctx?: Record<string, unknown>): void {
    LogService.info(`ComplianceV2(pkg): ${msg}`, ctx ?? {});
  },
  warn(msg: string, ctx?: Record<string, unknown>): void {
    LogService.info(`ComplianceV2(pkg): ${msg}`, ctx ?? {}, LogLevel.Warning);
  },
  error(msg: string, ctx?: Record<string, unknown>): void {
    LogService.info(`ComplianceV2(pkg): ${msg}`, ctx ?? {}, LogLevel.Error);
  },
};

/**
 * `ICompliance` view of the configured instance handed to the bus subscriber:
 * every call delegates to the SAME instance, with one POS-side tee on the
 * SALE outcome. The subscriber is fail-open and swallows outcomes, so
 * without the tee the POS would never see the SALE result — and that result
 * is the only carrier of the NF525 security code (EFR `Tag[Name='Sec']`, the
 * receipt's "Code:" line): the package's local receipt store and the PRINT
 * outcome both drop it (0.6.x, reported as a package gap). The tee routes a
 * successful SALE through the same bookkeeping the direct call used
 * (fiscal-receipt capture, EFR operator message, warnings). On a failed bus
 * SALE the order still completes (accepted fail-open tradeoff); the operator
 * is shown the error and the EFR-connection flag is refreshed so the
 * selling-disabled banner catches a dead EFR before the NEXT sale — but it
 * is NOT re-logged here: `BaseComplianceProvider.processEvent` already
 * emitted the stable Graylog error shape once.
 */
function withSaleOutcomeCapture(complianceV2: ComplianceV2): ICompliance {
  return {
    initialize: () => complianceV2.initialize(),
    getCompanyInfo: () => complianceV2.getCompanyInfo(),
    setCompanyInfo: (info) => complianceV2.setCompanyInfo(info),
    setLanguage: (language) => complianceV2.setLanguage(language),
    testComplianceDeviceConnection: () =>
      complianceV2.testComplianceDeviceConnection(),
    getCountry: () => complianceV2.getCountry(),
    async processEvent(type, payload) {
      // Confirming a reopened order completes an EMPTY edit session: there is
      // nothing to sign, and fiscalizing it would mint a phantom transaction
      // whose fresh TIK then hijacks the reprint path (the duplicate would
      // print as a first Vente with the live header).
      if (type === EventType.SALE) {
        const session = (payload as SalePayload).sale.toOrderSession();
        if (isEmptySessionForFiscalization(session)) {
          LogService.info("ComplianceV2: SALE skipped for an empty session", {
            orderSessionId: session.id,
          });
          return ok({ warnings: [] });
        }
      }
      const result = await complianceV2.processEvent(type, payload);
      if (type === EventType.SALE) {
        if (isErr(result)) {
          NotificationService.showCustomError(result.error.message);
          void testEfrConnection();
        } else {
          handleEventOutcomeV2((payload as SalePayload).sale.id, result.value);
        }
      }
      return result;
    },
  };
}

/**
 * Initializes the compliance-common-v2 library using its staged lifecycle:
 * construct → configure (device/org config). Device authentication happens
 * inside the WORK_IN event (its required `authToken`), not here — 0.6.0
 * removed the separate `authorize()` step.
 * France only: the v2 package's `ComplianceConfig.country` is limited to 'FR'.
 */
export async function initializeComplianceV2Lib(): Promise<void> {
  if (!isFrenchCompliant.value) {
    LogService.info(
      "ComplianceV2: Unsupported country, aborting initialization"
    );
    return;
  }
  // The user token is only consumed by WORK_IN (device auth), but its absence
  // means we are pre-login — configuring the lib then is pointless.
  if (!getUserAccessToken()) {
    LogService.info(
      "ComplianceV2: No token found when initializing compliance v2 lib, aborting"
    );
    return;
  }
  if (!DeviceModule.hardwareLocation?.deviceUid) {
    LogService.info("ComplianceV2: Failed to initialize, unregistered device");
    throw new Error("Failed to initialize compliance v2, unregistered device");
  }
  const posSwVersion: string = import.meta.env.VITE_APP_VERSION || "";
  if (!posSwVersion) {
    // configure() would reject an empty posSwVersion; fail with an explicit
    // message instead of the generic INVALID_CONFIG error.
    throw new Error(
      "ComplianceV2: VITE_APP_VERSION is not set, cannot initialize a certified France build"
    );
  }
  const smeetzAuth = await getSmtzAuth();
  if (!smeetzAuth) {
    throw new Error("No smtzAuth token found, aborting compliance v2 init");
  }

  const complianceV2 = new ComplianceV2();
  const configured = complianceV2.configure(
    {
      country: "FR",
      certificationServiceUrl: resolveEndpoint("VITE_CERTIFICATION_URL"),
      deviceUid: DeviceModule.hardwareLocation.deviceUid,
      language: getLanguage().toUpperCase(),
      posSwVersion,
      terminalId: DeviceModule.hardwareLocation.deviceUid,
      logServerUrl: import.meta.env.VITE_LOG_API,
      smeetzApiUrl: resolveEndpoint("VITE_GQL_API"),
      smeetzAuth,
      complianceEndpoint: EFR_SERVER_URL,
    },
    { counter: moduleTransactionCounter, logger: complianceV2Logger }
  );
  if (isErr(configured)) {
    LogService.info(
      "ComplianceV2: Failed to configure compliance v2 lib",
      { error: configured.error },
      LogLevel.Error
    );
    throw new Error(
      `ComplianceV2: configuration failed: ${configured.error.message}`
    );
  }

  // initialize() is a local echo of the device config in 0.4.x — logged for
  // parity with the v1 flow, never fatal.
  const initResult = await complianceV2.initialize();
  // A fresh instance has no WORK_IN session — drop the stale marker so the
  // next event restores the operator session (see operatorSessionRestore).
  operatorSessionRestore = null;
  // Bus wiring (attach once per configured instance, France-only by virtue
  // of the guard at the top): SALE on ORDER_SESSION_COMPLETED, NF525 323 on
  // ORDER_LINE_PRICE_OVERRIDDEN / ORDER_LINE_REMOVED. Detach the previous
  // instance's subscriptions first — the bus is shared and long-lived.
  for (const subscription of orderBusSubscriptions) {
    subscription.unsubscribe();
  }
  orderBusSubscriptions = new ComplianceOrderSubscriber(
    withSaleOutcomeCapture(complianceV2),
    complianceV2Logger
  ).attach(Order.events());
  ComplianceModule.setComplianceV2Obj({ complianceV2 });
  LogService.info("ComplianceV2: initialized", {
    deviceConfig: isErr(initResult) ? initResult.error : initResult.value,
  });
}

interface ComplianceV2Requirements {
  complianceV2: ComplianceV2;
  operator: Operator;
}

/**
 * Since 0.6.0 the acting operator is WORK_IN session state held IN MEMORY by
 * the package — an app reload (or a re-configure, which constructs a fresh
 * instance) drops it while the POS operator stays logged in, and every event
 * then fails OPERATOR_UNKNOWN. Tracks the package's session: null = none,
 * a promise = a WORK_IN was issued (pending or settled). Events restore the
 * session on demand through `validateComplianceV2Requirements`; sharing the
 * promise keeps concurrent events from firing duplicate WORK_INs.
 */
let operatorSessionRestore: Promise<void> | null = null;

/**
 * Validates everything a v2 fiscal event needs, initializing the library on
 * first use. Returns NULL outside France (v2 is France-only), throws when a
 * requirement is missing — in France a sale that cannot be fiscalized must
 * not complete. The fiscal transaction number is NOT validated here: the v2
 * package generates it itself through the injected counter port.
 *
 * Unless the caller IS the WORK_IN itself (`restoreOperatorSession: false`),
 * this also guarantees the package holds an operator session, re-sending a
 * WORK_IN when the in-memory session was lost (see `operatorSessionRestore`).
 */
async function validateComplianceV2Requirements(
  restoreOperatorSession = true
): Promise<ComplianceV2Requirements | null> {
  if (!isFrenchCompliant.value) {
    return null;
  }

  if (!DeviceModule.hardwareLocation?.deviceUid) {
    LogService.info(
      "ComplianceV2: Failed to process event, unregistered device"
    );
    throw new Error("Failed to process event, unregistered device");
  }

  if (!ComplianceModule.complianceV2) {
    await initializeComplianceV2Lib();
    if (!ComplianceModule.complianceV2) {
      LogService.info(
        "ComplianceV2: module is not initialized after re-trying to initialize it"
      );
      throw new Error("Compliance v2 module is not initialized.");
    }
  }

  if (!UserModule.userId) {
    throw new Error(
      "User ID is missing. Cannot proceed with event processing."
    );
  }

  if (restoreOperatorSession) {
    if (!operatorSessionRestore) {
      LogService.info(
        "ComplianceV2: no active operator session in the package, restoring with a WORK_IN"
      );
    }
    // processWorkEventV2 owns the shared marker: it reuses an in-flight or
    // settled WORK_IN and only signs a fresh one when there is none (or the
    // previous one failed) — so concurrent events can never double-LOGIN.
    await processWorkEventV2("in");
  }

  return {
    complianceV2: ComplianceModule.complianceV2,
    operator: {
      id: UserModule.userId.toString(),
      name: UserModule.user.fullname,
    },
  };
}

/**
 * Since 0.5.x the package normalizes order-common units (VAT fractions,
 * cent amounts) and coerces date strings itself — the session is passed
 * through unconverted. The only POS-side touch: an ABORTED session never
 * completed, so `sessionEnd` is still unset — stamp it with NOW so the
 * fiscal record carries the abandon moment instead of the session-creation
 * time the package would fall back to. (The bus-driven SALE needs no such
 * stamp: `ORDER_SESSION_COMPLETED` fires after the engine sets sessionEnd.)
 */
function withSessionEnd(session: OrderSession): OrderSession {
  return session.sessionEnd ? session : { ...session, sessionEnd: new Date() };
}

/**
 * An order session with no lines, payments, vouchers or discounts carries
 * nothing to fiscalize (same guard as the v1 sale path).
 */
function isEmptySessionForFiscalization(session: OrderSession): boolean {
  return (
    session.orderLines.length === 0 &&
    session.payments.length === 0 &&
    (session.vouchers?.length || 0) === 0 &&
    (session.orderDiscounts?.length || 0) === 0
  );
}

/**
 * Shared post-event bookkeeping. Transaction numbering is handled inside the
 * package (counter port) — only the receipt proof, the EFR's operator message
 * and warnings are handled here. `CERT_UNPERSISTED`/`AUTH_UNPERSISTED`
 * warnings are non-fatal: the EFR signed, the audit copy is queued for retry
 * on the package's sync port.
 */
function handleEventOutcomeV2(
  orderSessionId: string | undefined,
  outcome: EventOutcome
): void {
  if (orderSessionId && outcome.receipt) {
    // Never downgrade the stored receipt: 0.6.x PRINT outcomes carry no
    // securityCode (the package's receipt store drops it), while the SALE
    // outcome that landed first does — keep the best-known security code.
    const existing = ComplianceModule.fiscalReceipts[orderSessionId];
    ComplianceModule.setFiscalReceipt({
      orderSessionId,
      receipt: {
        ...outcome.receipt,
        securityCode: outcome.receipt.securityCode ?? existing?.securityCode,
      },
    });
  }
  // The EFR sometimes returns a message the operator must see (v1 parity:
  // `handleEventSent` shows `data.message`). compliance-common-v2 0.6.x does
  // not expose the EFR's success-path display message on `EventOutcome` yet
  // (reported as a package gap) — read it defensively so it surfaces the
  // moment the package adds it.
  const efrMessage = (outcome as EventOutcome & { message?: string }).message;
  if (efrMessage) {
    NotificationService.showCustomInfoWithTitle(
      efrMessage,
      t("compliance.efr-msg")
    );
  }
  for (const warning of outcome.warnings) {
    LogService.info(
      `ComplianceV2: ${POSConfigModule.getGroupCountryCode} event warning`,
      { warning },
      LogLevel.Warning
    );
  }
}

function notifyComplianceV2Error(error: ComplianceError): void {
  // The alpha2 country code in the message is what routes these to the right
  // channel from Graylog (v1 parity) — France-only today, but the v2 package
  // will grow more countries.
  LogService.info(
    `ComplianceV2: error processing ${POSConfigModule.getGroupCountryCode} compliance event`,
    { error },
    LogLevel.Error
  );
  NotificationService.showCustomError(error.message);
  // Refresh the EFR connection flag after a fiscal failure so the selling-
  // disabled banner reflects reality (same recovery path as the v1 events).
  void testEfrConnection();
}

/**
 * Journals the abandon (ABORT) of a non-completed session before it is
 * dropped. Fail-open on purpose: a fiscal error must not block the cashier
 * from closing the tab.
 */
export async function processAbortEventV2(order: OrderJson): Promise<void> {
  try {
    const requirements = await validateComplianceV2Requirements();
    if (!requirements) {
      return;
    }

    const session = order.orderSession;
    if (isEmptySessionForFiscalization(session)) {
      return;
    }

    const payload: AbortPayload = {
      sale: new FiscalSale(withSessionEnd(session)),
    };

    LogService.info("ComplianceV2: Processing ABORT event", {
      orderId: order.id,
      orderSessionId: session.id,
    });

    const result = await requirements.complianceV2.processEvent(
      EventType.ABORT,
      payload
    );
    if (isErr(result)) {
      notifyComplianceV2Error(result.error);
      return;
    }

    handleEventOutcomeV2(session.id, result.value);
    LogService.info("ComplianceV2: ABORT processed successfully", {
      orderSessionId: session.id,
    });
  } catch (error) {
    LogService.info(
      "ComplianceV2: Failed to process ABORT event",
      { error },
      LogLevel.Warning
    );
  }
}

/**
 * Fiscalizes a France audit-record event (NF525 JET): drawer open/close,
 * price lookup, suspend/resume, data export/archive, operator-rights change
 * (130). Throws on fiscal failure — same contract as the v1
 * `processAdditionalEvents`.
 */
export async function processAuditEventV2(
  type: AuditEventType,
  reason?: string
): Promise<EventOutcome | undefined> {
  const requirements = await validateComplianceV2Requirements();
  if (!requirements) {
    return undefined;
  }

  const payload: AuditEventPayload = { reason };
  LogService.info("ComplianceV2: Processing audit event", { type });

  const result = await requirements.complianceV2.processEvent(type, payload);
  if (isErr(result)) {
    notifyComplianceV2Error(result.error);
    throw new Error(result.error.message);
  }

  handleEventOutcomeV2(undefined, result.value);
  LogService.info("ComplianceV2: audit event processed successfully", {
    type,
  });
  return result.value;
}

/**
 * Journals a team role change made from the till: NF525 audit record 130,
 * "Modification des droits opérateur". Operator rights are part of the fiscal
 * record, so changing someone's role has to reach the JET.
 *
 * Emitted AFTER the role has been saved and deliberately non-fatal: the change
 * is already persisted server-side by the time this runs, so a failed journal
 * entry must never be reported to the operator as a failed role change.
 * Callers get `false` back when nothing was journalled, and warn instead.
 *
 * @param {object} data - Who was changed, and from which role to which.
 * @returns {Promise<boolean>} true when the audit record was written.
 */
export async function processRoleChangeEventV2(data: {
  memberName: string;
  previousRoleName: string;
  newRoleName: string;
}): Promise<boolean> {
  if (!isFrenchCompliant.value) {
    return false;
  }

  try {
    const outcome = await processAuditEventV2(
      EventType.OPERATOR_RIGHTS_CHANGE,
      `Changement de rôle: ${data.memberName} (${data.previousRoleName} -> ${data.newRoleName})`
    );

    LogService.info("ComplianceV2: team role change journalled", { data });

    return !!outcome;
  } catch (error) {
    // Unregistered device, EFR down, missing operator session: the role change
    // itself already succeeded, so only the journalling is reported as failed.
    LogService.info(
      "ComplianceV2: team role change could not be journalled",
      { data, error },
      LogLevel.Error
    );

    return false;
  }
}

/**
 * Records a receipt print for the session and returns its fiscal proof:
 * the first print returns the cert-stored fiscal info, a repeat print is
 * re-issued as a duplicata through the EFR. Never throws — printing a
 * receipt must not block on the fiscal layer; the caller falls back to the
 * locally captured fiscal receipt.
 */
export async function processPrintEventV2(
  orderSessionId: string
): Promise<EventOutcome | undefined> {
  try {
    const requirements = await validateComplianceV2Requirements();
    if (!requirements) {
      return undefined;
    }

    const payload: PrintPayload = { orderSessionId };
    LogService.info("ComplianceV2: Processing PRINT event", {
      orderSessionId,
    });

    const result = await requirements.complianceV2.processEvent(
      EventType.PRINT,
      payload
    );
    if (isErr(result)) {
      LogService.info(
        "ComplianceV2: PRINT event failed",
        { orderSessionId, error: result.error },
        LogLevel.Warning
      );
      return undefined;
    }

    handleEventOutcomeV2(orderSessionId, result.value);
    LogService.info("ComplianceV2: PRINT processed successfully", {
      orderSessionId,
      receipt: result.value.receipt,
    });
    return result.value;
  } catch (error) {
    LogService.info(
      "ComplianceV2: Failed to process PRINT event",
      { error },
      LogLevel.Warning
    );
    return undefined;
  }
}

/**
 * Fiscalizes the France operator login (WORK_IN) / logout (WORK_OUT).
 * WORK_IN is the session-start: it carries the operator plus a fresh auth
 * token that authenticates the device fail-closed; WORK_OUT takes no payload,
 * the package signs off the operator it resolved from the WORK_IN session.
 * Throws on fiscal failure — same contract as the v1 `processAdditionalEvents`.
 *
 * One WORK_IN per operator session: the explicit login call, the lazy
 * event-time restore (`validateComplianceV2Requirements`) and the proactive
 * restore (`ensureOperatorSessionV2`) all funnel through the shared marker,
 * which is claimed SYNCHRONOUSLY here — two concurrent callers can never
 * sign duplicate LOGIN entries into the JET.
 */
export async function processWorkEventV2(
  direction: "in" | "out"
): Promise<EventOutcome | undefined> {
  if (direction === "out") {
    return sendWorkEventV2("out");
  }
  if (operatorSessionRestore) {
    try {
      // An equivalent WORK_IN already ran or is in flight — reuse it.
      await operatorSessionRestore;
      return undefined;
    } catch {
      // The previous WORK_IN failed — clear the marker and sign a fresh one.
      operatorSessionRestore = null;
    }
  }
  const workIn = sendWorkEventV2("in");
  // Claimed before the first await above resolves: callers that land while
  // this WORK_IN is in flight await it instead of issuing their own.
  operatorSessionRestore = workIn.then(() => undefined);
  try {
    return await workIn;
  } catch (error) {
    // A failed WORK_IN must not poison later attempts.
    operatorSessionRestore = null;
    throw error;
  }
}

/** The actual WORK event send — marker bookkeeping lives in the caller. */
/**
 * Mirror the organizer's legal identity from the library into the POS config.
 *
 * The French receipt prints the SIRET, SIREN, NAF code, capital and legal
 * form, and reads them from `POSConfigModule.companyLegalInfo`. Since 0.7.0
 * the library fetches that record from the certification service itself on
 * WORK_IN (and configures the EFR with it), so the POS only has to copy the
 * result across instead of querying the service on its own.
 *
 * Silent by design: `getCompanyInfo()` throws until the library holds a
 * record, and a missing company block must never break signing in.
 *
 * @param {ComplianceV2} complianceV2 - The configured library instance.
 * @returns {void}
 */
/**
 * The certified company record straight from the live compliance package —
 * null before WORK_IN has resolved it (the package throws until then) or when
 * the library is not initialized. Receipts prefer this over the store copy.
 */
export function getCertifiedCompanyInfoV2(): CompanyLegalInfo | null {
  try {
    return ComplianceModule.complianceV2?.getCompanyInfo() ?? null;
  } catch {
    return null;
  }
}

function syncCompanyLegalInfoV2(complianceV2: ComplianceV2): void {
  try {
    const companyLegalInfo = complianceV2.getCompanyInfo();
    if (companyLegalInfo) {
      POSConfigModule.setCompanyLegalInfo(companyLegalInfo);
      LogService.info("ComplianceV2: company legal info synced", {
        companyLegalInfo,
      });
    }
  } catch (error) {
    LogService.info(
      "ComplianceV2: no company legal info held by the library yet",
      { error }
    );
  }
}

async function sendWorkEventV2(
  direction: "in" | "out"
): Promise<EventOutcome | undefined> {
  // WORK_IN must not trigger the session restore — it IS the restore.
  // WORK_OUT keeps it, so a logout after a reload still signs a LOGIN/LOGOUT
  // pair instead of failing OPERATOR_UNKNOWN.
  const requirements = await validateComplianceV2Requirements(
    direction !== "in"
  );
  if (!requirements) {
    return undefined;
  }

  const type = direction === "in" ? EventType.WORK_IN : EventType.WORK_OUT;
  LogService.info("ComplianceV2: Processing WORK event", { type });

  let result;
  if (direction === "in") {
    const token = getUserAccessToken();
    if (!token) {
      LogService.info(
        "ComplianceV2: No token found when processing WORK_IN, aborting"
      );
      throw new Error("No token found when processing WORK_IN");
    }
    result = await requirements.complianceV2.processEvent(EventType.WORK_IN, {
      operator: requirements.operator,
      authToken: token,
    });
  } else {
    result = await requirements.complianceV2.processEvent(
      EventType.WORK_OUT,
      {}
    );
  }
  if (isErr(result)) {
    notifyComplianceV2Error(result.error);
    throw new Error(result.error.message);
  }

  if (direction === "out") {
    // Signed off — the package holds no operator session any more.
    operatorSessionRestore = null;
  } else {
    // WORK_IN is where the library resolves the device's certification
    // record, so this is the first moment the company block is available.
    syncCompanyLegalInfoV2(requirements.complianceV2);
  }

  handleEventOutcomeV2(undefined, result.value);
  LogService.info("ComplianceV2: WORK event processed successfully", { type });
  return result.value;
}

/**
 * Proactively restores the package's in-memory operator session (a WORK_IN)
 * instead of waiting for the first fiscal event to need it — called on
 * `sessionReady` and after every re-configure retry. The direct events
 * restore lazily through `validateComplianceV2Requirements`, but the
 * bus-driven SALE cannot: `ComplianceOrderSubscriber` is fail-open, so an
 * OPERATOR_UNKNOWN inside the bus is only logged and the sale would complete
 * silently unfiscalized. Shares the WORK_IN marker with the explicit login
 * call — it never signs a duplicate LOGIN. Fail-soft by contract: errors are
 * logged, never thrown; the lazy per-event restore remains the safety net
 * for the direct events.
 */
export async function ensureOperatorSessionV2(): Promise<void> {
  // Outside France there is no v2 session; without a user token a WORK_IN
  // cannot authenticate the device — both are normal pre-login states.
  if (!isFrenchCompliant.value || !getUserAccessToken()) {
    return;
  }
  try {
    await processWorkEventV2("in");
  } catch (error) {
    LogService.info(
      "ComplianceV2: proactive operator-session restore failed",
      { error },
      LogLevel.Warning
    );
  }
}

/**
 * Maps the v1 `MoneyInOutType` (used by every POS modal) to the v2 enum.
 * The v1 members `DEPOSIT_CHANGE` and `DAILY_CLOSURE` share the runtime value
 * 'TRANSFER', so they are disambiguated by the amount sign — positive =
 * deposit, negative = closure — exactly as the EFSTA spec does.
 */
function mapMoneyInOutTypeToV2(
  type: MoneyInOutTypeV1,
  amount: number
): MoneyInOutTypeV2 {
  switch (String(type)) {
    case "INI":
      return MoneyInOutTypeV2.OPENING_BALANCE;
    case "TRANSFER":
      return amount >= 0
        ? MoneyInOutTypeV2.DEPOSIT_CHANGE
        : MoneyInOutTypeV2.DAILY_CLOSURE;
    case "PAY_IN":
      return MoneyInOutTypeV2.PAY_IN;
    case "PAY_OUT":
      return MoneyInOutTypeV2.PAY_OUT;
    case "DIFFERENCE":
    default:
      return MoneyInOutTypeV2.DIFFERENCE;
  }
}

/**
 * Fiscalizes a France cash movement (money in/out) through the v2 package.
 * Sign normalization (PAY_IN forced positive, PAY_OUT forced negative,
 * DIFFERENCE sign-driven) is handled inside the package — amounts are passed
 * through as the POS recorded them. Throws on fiscal failure.
 *
 * v1 also carried `paymentType` and `cashLocation`; the v2 France payload has
 * no slot for them (package contract, FIS-486).
 */
export async function processMoneyInOutEventV2(data: {
  moneyInOutType: MoneyInOutTypeV1;
  amount: number;
  description: string;
  posDateTime?: string;
  reason?: string;
}): Promise<EventOutcome | undefined> {
  const requirements = await validateComplianceV2Requirements();
  if (!requirements) {
    return undefined;
  }

  const payload: MoneyInOutPayload = {
    type: mapMoneyInOutTypeToV2(data.moneyInOutType, data.amount),
    amount: data.amount,
    description: data.description,
    reason: data.reason,
    posDateTime: data.posDateTime ? new Date(data.posDateTime) : undefined,
  };

  LogService.info("ComplianceV2: Processing MONEY_IN_OUT event", {
    type: payload.type,
    amount: payload.amount,
    description: payload.description,
  });

  const result = await requirements.complianceV2.processEvent(
    EventType.MONEY_IN_OUT,
    payload
  );
  if (isErr(result)) {
    notifyComplianceV2Error(result.error);
    throw new Error(result.error.message);
  }

  handleEventOutcomeV2(undefined, result.value);
  LogService.info("ComplianceV2: MONEY_IN_OUT processed successfully", {
    type: payload.type,
  });
  return result.value;
}

/**
 * Fiscalizes the France daily closure (Z report). Throws on fiscal failure —
 * the call site (cashfloat close) catches and logs, matching the v1 contract.
 */
export async function processReportDailyEventV2(): Promise<
  EventOutcome | undefined
> {
  const requirements = await validateComplianceV2Requirements();
  if (!requirements) {
    return undefined;
  }

  const payload: ReportDailyPayload = {};
  LogService.info("ComplianceV2: Processing REPORT_DAILY (Z) event");

  const result = await requirements.complianceV2.processEvent(
    EventType.REPORT_DAILY,
    payload
  );
  if (isErr(result)) {
    notifyComplianceV2Error(result.error);
    throw new Error(result.error.message);
  }

  handleEventOutcomeV2(undefined, result.value);
  LogService.info("ComplianceV2: REPORT_DAILY processed successfully");
  return result.value;
}
